import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  className,
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = false,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={cn('space-y-1.5', fullWidth && 'w-full')}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-[var(--text)]"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {leftIcon}
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'block w-full h-9 rounded-md border bg-[var(--surface)] px-3 text-sm text-[var(--text)] placeholder-[var(--muted)] transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]',
            error
              ? 'border-status-error focus:ring-status-error'
              : 'border-[var(--border)] hover:border-[var(--accent)]/60',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            className
          )}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)]">
            {rightIcon}
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-xs text-status-error font-medium">{error}</p>
      )}

      {helperText && !error && (
        <p className="text-xs text-[var(--muted)]">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
export { Input };
